'use client';

import classnames from 'classnames';
import { useSportsDataApi } from '@stadionhq-v3/hooks';
import { ReactNode, useEffect } from 'react';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import { Loading } from '@stadionhq-v3/base-ui';
import {
  MatchResult,
  Result,
} from '@components/v3/molecules/combined-form-guide/combinedFormGuide.types';
import { BuildUpBlockWrapper, EmptyState } from '@stadionhq-v3/football-ui';
import CombinedFormGuide from '@components/v3/molecules/combined-form-guide';
import {
  ClubFormGuide,
  FormGuideApiResponse,
  FormGuideLabels,
  FormGuideRequestProps,
} from './formGuideRequest.types';
import styles from './formGuideRequest.module.scss';

const CombinedFormGuideTeamComponent = ({
  teamName,
  oppositionTeamName,
  teamCrest,
  oppositionTeamCrest,
  homeTeamFormGuide,
  oppositionTeamFormGuide,
  labels,
  showLeagueStats,
  primaryTeam,
}: {
  teamName: string;
  teamCrest: ReactNode;
  oppositionTeamName: string;
  oppositionTeamCrest: ReactNode;
  homeTeamFormGuide?: ClubFormGuide;
  oppositionTeamFormGuide?: ClubFormGuide;
  labels: FormGuideLabels;
  showLeagueStats?: boolean;
  primaryTeam?: 'away' | 'home';
}) => {
  if (
    !homeTeamFormGuide?.club ||
    !homeTeamFormGuide ||
    !oppositionTeamFormGuide?.club ||
    !oppositionTeamFormGuide
  ) {
    return null;
  }

  const {
    club: homeTeamClub,
    recentForm: homeTeamRecentForm,
    leaguePosition: homeTeamLeaguePosition,
    leaguePlayed: homeTeamLeaguePlayed,
    leaguePoints: homeTeamLeaguePoints,
  } = homeTeamFormGuide;
  const {
    club: awayTeamClub,
    recentForm: awayTeamRecentForm,
    leaguePosition: awayTeamLeaguePosition,
    leaguePlayed: awayTeamLeaguePlayed,
    leaguePoints: awayTeamLeaguePoints,
  } = oppositionTeamFormGuide;

  const { crestImageUrl: homeTeamCrestImageUrl, name: homeTeamName } =
    homeTeamClub;
  const { crestImageUrl: awayTeamCrestImageUrl, name: awayTeamName } =
    awayTeamClub;

  const homeTeamCrest = homeTeamCrestImageUrl ? (
    <ContentfulImage
      url={homeTeamCrestImageUrl}
      alt={homeTeamName ?? teamName}
      height={52}
      width={52}
    />
  ) : (
    teamCrest
  );

  const awayTeamCrest = awayTeamCrestImageUrl ? (
    <ContentfulImage
      url={awayTeamCrestImageUrl}
      alt={awayTeamName ?? oppositionTeamName}
      height={52}
      width={52}
    />
  ) : (
    oppositionTeamCrest
  );

  const homeTeamResults: MatchResult[] = homeTeamRecentForm.map(
    (form, index) => ({ id: index, result: form as Result })
  );
  const awayTeamResults: MatchResult[] = awayTeamRecentForm.map(
    (form, index) => ({ id: index, result: form as Result })
  );

  const {
    formLabel,
    winAbbreviated,
    lossAbbreviated,
    drawAbbreviated,
    positionHeading,
    pointsHeading,
    playedHeading,
  } = labels;

  return (
    <CombinedFormGuide
      homeTeamCrest={homeTeamCrest}
      awayTeamCrest={awayTeamCrest}
      homeTeamName={homeTeamName ?? ''}
      awayTeamName={awayTeamName ?? ''}
      homeTeamLeaguePlayed={homeTeamLeaguePlayed?.toString() ?? ''}
      awayTeamLeaguePlayed={awayTeamLeaguePlayed?.toString() ?? ''}
      homeTeamLeaguePoints={homeTeamLeaguePoints?.toString() ?? ''}
      awayTeamLeaguePoints={awayTeamLeaguePoints?.toString() ?? ''}
      homeTeamLeaguePosition={homeTeamLeaguePosition?.toString() ?? ''}
      awayTeamLeaguePosition={awayTeamLeaguePosition?.toString() ?? ''}
      winAbbreviated={winAbbreviated}
      lossAbbreviated={lossAbbreviated}
      drawAbbreviated={drawAbbreviated}
      positionHeading={positionHeading}
      pointsHeading={pointsHeading}
      playedHeading={playedHeading}
      homeTeamResults={homeTeamResults}
      awayTeamResults={awayTeamResults}
      showLeagueStats={showLeagueStats}
      formLabel={formLabel}
      primaryTeam={primaryTeam}
    />
  );
};

const FormGuideRequest = ({
  title,
  sponsor,
  SPORTS_DATA_API,
  competition,
  dictionary,
  oppositionName,
  oppositionCrest,
  opposition,
  teamName,
  teamCrest,
  team,
  toDate,
  prevSeasonId,
  currentSeasonId,
  nativeProps,
  showLeagueStats,
  classNames,
  primaryTeam,
}: FormGuideRequestProps) => {
  const classes = classnames(
    [styles.tokens, styles.formGuideRequest],
    classNames
  );

  const { BASE_URL, ENDPOINTS, LOCALE } = SPORTS_DATA_API;

  const buildUrl = (seasonId: string) => {
    const url = new URL(`${BASE_URL}/${ENDPOINTS.FORM_GUIDE}`);

    const queryParams = new URLSearchParams({
      seasonId,
      teamId: team.id,
      oppositionTeamId: opposition.id,
      competitionId: competition.id,
      toDate,
      ...(LOCALE ? { locale: LOCALE } : {}),
    });

    url.search = queryParams.toString();

    return url;
  };

  const {
    errorMessage: currentSeasonErrorMessage,
    isLoading: isCurrentSeasonLoading,
    data: currentSeasonData,
    fetchItems: fetchCurrentSeasonData,
  } = useSportsDataApi<FormGuideApiResponse>({
    url: buildUrl(currentSeasonId),
    dictionary,
    catch404: true,
  });

  const {
    errorMessage: prevSeasonErrorMessage,
    isLoading: isPrevSeasonLoading,
    data: prevSeasonData,
    fetchItems: fetchPrevSeasonData,
  } = useSportsDataApi<FormGuideApiResponse>({
    url: buildUrl(prevSeasonId),
    dictionary,
    catch404: true,
  });

  useEffect((): undefined | (() => void) => {
    const controller = new AbortController();

    fetchCurrentSeasonData(controller);
    fetchPrevSeasonData(controller);

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = isCurrentSeasonLoading || isPrevSeasonLoading;

  const errorMessage = currentSeasonErrorMessage || prevSeasonErrorMessage;

  const homeTeamFormData = currentSeasonData?.clubFormGuide?.recentForm
    ? currentSeasonData
    : prevSeasonData;
  const awayTeamFormData = currentSeasonData?.opponentClubFormGuide?.recentForm
    ? currentSeasonData
    : prevSeasonData;

  const hasResults = Boolean(
    homeTeamFormData?.clubFormGuide?.recentForm ||
    awayTeamFormData?.clubFormGuide?.recentForm
  );

  return (
    <div {...nativeProps} className={classes}>
      {isLoading ? (
        <Loading
          label={dictionary.loading}
          classNames={[classnames(styles.loading)]}
        />
      ) : errorMessage ? (
        <EmptyState
          {...errorMessage}
          classNames={[classnames(styles.apiMessage)]}
        />
      ) : hasResults ? (
        <BuildUpBlockWrapper title={title} sponsor={sponsor}>
          <div className={classnames(styles.formGuideBlock)}>
            <CombinedFormGuideTeamComponent
              teamName={teamName}
              oppositionTeamName={oppositionName}
              teamCrest={teamCrest}
              oppositionTeamCrest={oppositionCrest}
              homeTeamFormGuide={homeTeamFormData?.clubFormGuide}
              oppositionTeamFormGuide={awayTeamFormData?.opponentClubFormGuide}
              labels={dictionary.formGuideLabels}
              showLeagueStats={showLeagueStats}
              primaryTeam={primaryTeam}
            />
          </div>
        </BuildUpBlockWrapper>
      ) : null}
    </div>
  );
};

export default FormGuideRequest;
